import {
  Container,
  EditIconLabelWrapper,
  Grid,
  HorizontalLine,
  LabelKey,
  LabelValue,
  NameLabel,
  PriceLabel,
  RemoveIconLabelWrapper,
  RowSpaceBetween,
  FirstRow,
  RightPart,
  LeftPart,
  DuplicateIconLabelWrapper,
  ImagesConfigurationWrapper,
  GridPriceWrapper,
  PriceWrapper,
  BottomWrapper,
  QuantityWrapper,
  QuantityLabel,
  GrossPriceValue,
  NetPriceValue,
  DiscountWrapper,
  DiscountType,
  DiscountTypeLabel,
  DiscountInput,
  QuantityValue,
  LabelKeyWrapper,
  LinkedOrderLabel,
  LinkedOrderValue,
} from './ShoppingCartCard.styled';
import {
  useChangeDiscountCartLine,
  useChangeQuantityCartLine,
  useDuplicateCartLine,
} from './hooks';
import { parseQuestionsObjectResponseIntoArray } from 'store/Model/reducers/model';
import { handleTrueFalseLabel } from './helpers';
import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';
import { setImages, setPricing, setQuestions } from 'store/Model/actions/model';
import { setCurrentlyEditingShoppingCartLineData } from 'store/ShoppingCart/actions/shoppingCart';
import { setIndicativeQuestionKeys } from 'store/SubmitConfiguration/actions/submitConfiguration';
import { deriveIndicativeQuestionKeys } from 'providers/QueryProvider/helpers';
import FormImages from 'pages/ConfigurePage/FormImages/FormImages';
import {
  IImageSection,
  useFilterOutHiddenImages,
} from 'pages/ConfigurePage/FormImages/hooks';
import Icon from 'components/Icon/Icon';
import {
  Copy,
  GearSix,
  PencilSimple,
  Trash,
  Question as QuestionIcon,
} from '@phosphor-icons/react';
import { COLORS } from 'assets/styled';
import { formatValuesFromOutputs } from 'pages/ConfigurePage/ConfigureForm/helpers';
import { IQuestion } from 'types/Question.types';
import { ICartItem } from 'types/CartItem.types';
import { useGetValuesFromQuestionOutputs } from 'pages/ConfigurePage/ConfigureForm/hooks';
import Input from 'components/Input/Input';
import { useCallback, useState } from 'react';
import debounce from 'lodash.debounce';
import Tooltip from 'components/Tooltip/Tooltip';
import { DiscountsType } from 'components/Modals/EditDiscountModal/EditDiscountModal';
import { useActiveCartId } from 'utils/hooks/useActiveCartId';
import {
  setActiveTab,
  setOrderProductsStep,
  setProductId,
} from 'store/Order/actions/order';
import { OrderProductsSteps, OrderTabsEnum } from 'types/Order.types';
import { useFormatMoney } from 'utils/hooks/useFormatMoney';

interface IShoppingCartCardProps {
  item: ICartItem;
  onRemove?: () => void;
  isPreview?: boolean;
  buyerMode?: boolean;
  isGrossVisible: boolean;
  currency: string;
  onConfigure?: () => void;
}

const ShoppingCartCard = ({
  item,
  onRemove,
  isPreview = false,
  buyerMode = false,
  isGrossVisible,
  currency,
  onConfigure,
}: IShoppingCartCardProps) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const formatMoney = useFormatMoney(currency);
  const shoppingCartId = useActiveCartId();
  const [quantity, setQuantity] = useState<string>(item.quantity.toString());
  const [discount, setDiscount] = useState<string>(() => {
    if (!item.discount_amount) return '';
    // Convert decimal to percentage if it's a percentage discount
    return item.discount_type === DiscountsType.PERCENTAGE
      ? (Number(item.discount_amount) * 100).toFixed(2)
      : item.discount_amount;
  });
  const [discountType, setDiscountType] = useState<DiscountsType>(
    item.discount_type ?? DiscountsType.AMOUNT
  );

  const questions: IQuestion[] = parseQuestionsObjectResponseIntoArray(
    item.configuration.questions
  );
  const valuesFromOutputs = useGetValuesFromQuestionOutputs(questions, true);
  const formattedValuesFromOutputs = formatValuesFromOutputs(
    valuesFromOutputs,
    questions
  );

  // Quotation lines can mark questions as indicative (value not final)
  const indicativeQuestionNames = new Set(
    questions
      .filter((question: IQuestion) => question.indicative)
      .map((question: IQuestion) => question.name)
  );

  const { mutate: duplicateCartLine } = useDuplicateCartLine();
  const {
    mutate: changeQuantityCartLine,
    isLoading: isLoadingChangeQuantityCartLine,
  } = useChangeQuantityCartLine(item.id);

  const { mutate: changeDiscountCartLine } = useChangeDiscountCartLine(
    shoppingCartId,
    item.id
  );

  // Quantity change for arrows
  const debouncedChangeQuantity = useCallback(
    debounce((value: number) => {
      changeQuantityCartLine(value);
    }, 500),
    [changeQuantityCartLine]
  );

  const hasImages =
    item.configuration.images && Object.keys(item.configuration.images).length;

  const visibleImages: IImageSection[] = useFilterOutHiddenImages(
    item.configuration.images
  );

  return (
    <Container>
      <FirstRow>
        <LeftPart>
          <NameLabel>{item.configuration.general.name}</NameLabel>
          {isPreview && <QuantityValue>x{item.quantity}</QuantityValue>}
          {isPreview && item.linked_order_number && (
            <>
              <LinkedOrderLabel>{t('Linked to')}:</LinkedOrderLabel>
              <LinkedOrderValue>{item.linked_order_number}</LinkedOrderValue>
            </>
          )}
        </LeftPart>
        {isPreview && onConfigure && (
          <RightPart>
            <Tooltip content={t('Configure indicative questions')}>
              <EditIconLabelWrapper onClick={onConfigure}>
                <Icon svg={GearSix} color={COLORS.GREEN} onClick={() => null} />
              </EditIconLabelWrapper>
            </Tooltip>
          </RightPart>
        )}
        {!isPreview && (
          <RightPart>
            <EditIconLabelWrapper
              onClick={() => {
                if (!isLoadingChangeQuantityCartLine) {
                  dispatch(setQuestions(item.configuration.questions));
                  dispatch(
                    setIndicativeQuestionKeys(
                      deriveIndicativeQuestionKeys(item.configuration.questions)
                    )
                  );
                  dispatch(setImages(item.configuration.images));
                  dispatch(setPricing(item.configuration.pricing));
                  dispatch(
                    setCurrentlyEditingShoppingCartLineData(
                      item.id,
                      Number(quantity)
                    )
                  );
                  dispatch(
                    setOrderProductsStep(OrderProductsSteps.Configuration)
                  );
                  dispatch(setProductId(item.model_id.toString()));
                  dispatch(setActiveTab(OrderTabsEnum.Products));
                }
              }}
            >
              <Icon
                disabled={isLoadingChangeQuantityCartLine}
                svg={PencilSimple}
                color={
                  isLoadingChangeQuantityCartLine
                    ? COLORS.GRAY_200
                    : COLORS.GREEN
                }
                onClick={() => null}
              />
            </EditIconLabelWrapper>
            <DuplicateIconLabelWrapper
              onClick={() =>
                duplicateCartLine({
                  cartLineId: item.id,
                  shoppingCartId: shoppingCartId,
                })
              }
            >
              <Icon svg={Copy} color={COLORS.GREEN} onClick={() => null} />
            </DuplicateIconLabelWrapper>
            <RemoveIconLabelWrapper onClick={() => onRemove?.()}>
              <Icon svg={Trash} color={COLORS.RED_100} onClick={() => null} />
            </RemoveIconLabelWrapper>
          </RightPart>
        )}
      </FirstRow>
      <ImagesConfigurationWrapper>
        {hasImages ? (
          <FormImages visibleImages={visibleImages} singleImage disableSticky />
        ) : null}
        <GridPriceWrapper>
          <Grid hasImages={hasImages}>
            {Object.keys(formattedValuesFromOutputs)?.map(
              (dataKey: string, index: number) => {
                const rawValue =
                  formattedValuesFromOutputs[dataKey]?.toString() ?? '';
                const displayValue = handleTrueFalseLabel(rawValue, t);
                const isIndicative = indicativeQuestionNames.has(dataKey);

                return (
                  <RowSpaceBetween key={index}>
                    <LabelKeyWrapper>
                      <LabelKey>{dataKey}</LabelKey>
                      <Tooltip content={isIndicative ? t('Indicative') : ''}>
                        {isIndicative && (
                          <Icon
                            svg={QuestionIcon}
                            size={20}
                            color={COLORS.GREEN}
                          />
                        )}
                      </Tooltip>
                    </LabelKeyWrapper>
                    {displayValue.length > 15 ? (
                      <Tooltip content={displayValue}>
                        <LabelValue isIndicative={isIndicative}>
                          {`${displayValue.slice(0, 15)}...`}
                        </LabelValue>
                      </Tooltip>
                    ) : (
                      <LabelValue isIndicative={isIndicative}>
                        {displayValue}
                      </LabelValue>
                    )}
                  </RowSpaceBetween>
                );
              }
            )}
          </Grid>
          <BottomWrapper>
            {/* {!isPreview && !buyerMode && (
              <>
                <DiscountWrapper>
                  <DiscountType>
                    <DiscountTypeLabel
                      isActiveDiscountType={
                        discountType === DiscountsType.AMOUNT
                      }
                      onClick={() => {
                        setDiscountType(DiscountsType.AMOUNT);
                        if (discount !== '') {
                          changeDiscountCartLine({
                            discountAmount: null,
                            discountType: discountType,
                          });
                        }
                        setDiscount('');
                      }}
                    >
                      {t('Amount')}
                    </DiscountTypeLabel>
                    <DiscountTypeLabel
                      isActiveDiscountType={
                        discountType === DiscountsType.PERCENTAGE
                      }
                      onClick={() => {
                        setDiscountType(DiscountsType.PERCENTAGE);
                        if (discount !== '') {
                          changeDiscountCartLine({
                            discountAmount: null,
                            discountType: discountType,
                          });
                        }
                        setDiscount('');
                      }}
                    >
                      {t('Percentage')}
                    </DiscountTypeLabel>
                  </DiscountType>
                  <DiscountInput>
                    <Input
                      label=""
                      placeholder={t('Discount')}
                      value={discount}
                      errorMessage={''}
                      width={'100%'}
                      icon={
                        discountType === DiscountsType.PERCENTAGE
                          ? Percent
                          : CurrencyEur
                      }
                      hideNumberArrows
                      type="text"
                      wrapperStyles={{ width: '100%', maxWidth: '300rem' }}
                      onChange={(e) => {
                        const rawValue = e.target.value;
                        const normalizedValue = rawValue.replace(',', '.');

                        if (normalizedValue.includes('-')) {
                          return;
                        }

                        const regex = /^-?\d*\.?\d*$/;

                        if (
                          normalizedValue === '' ||
                          regex.test(normalizedValue)
                        ) {
                          setDiscount(normalizedValue);
                        }
                      }}
                      onBlur={() => {
                        const normalized = discount.replace(',', '.');
                        const number = parseFloat(normalized);

                        if (!discount || discount === '-' || isNaN(number)) {
                          setDiscount('');
                          changeDiscountCartLine({
                            discountAmount: null,
                            discountType: discountType,
                          });
                          return;
                        }

                        const clampedValue =
                          discountType === DiscountsType.PERCENTAGE
                            ? Math.min(Math.max(number, 0), 100)
                            : number;

                        // For display we show the full percentage (e.g., 50), but send the decimal (0.5)
                        const valueToSend =
                          discountType === DiscountsType.PERCENTAGE
                            ? (clampedValue / 100).toFixed(2)
                            : clampedValue.toFixed(2);

                        setDiscount(clampedValue.toFixed(2));
                        changeDiscountCartLine({
                          discountAmount: valueToSend,
                          discountType: discountType,
                        });
                      }}
                    />
                  </DiscountInput>
                </DiscountWrapper>
              </>
            )} */}
            {!isPreview && (
              <QuantityWrapper>
                <QuantityLabel>{t('Quantity')}:</QuantityLabel>
                <Input
                  label=""
                  placeholder=""
                  width={'90rem'}
                  type="number"
                  value={quantity}
                  onChange={(e) => {
                    const value = e.target.value;
                    if (value === '') {
                      setQuantity(value);
                      return;
                    }
                    const number = Number(value);
                    if (isNaN(number) || number < 0) {
                      return;
                    }
                    setQuantity(value);
                  }}
                  onBlur={() => {
                    debouncedChangeQuantity.cancel(); // Avoid double requests

                    let value = parseInt(quantity, 10);
                    if (isNaN(value) || value < 1) value = 1;
                    setQuantity(value.toString());
                    if (
                      value !== item.quantity &&
                      !isLoadingChangeQuantityCartLine
                    ) {
                      changeQuantityCartLine(value);
                    }
                  }}
                  onIncrease={() => {
                    let value = parseInt(quantity, 10);
                    if (isNaN(value) || value < 1) value = 1;
                    const newValue = value + 1;
                    setQuantity(newValue.toString());
                    debouncedChangeQuantity(newValue);
                  }}
                  onDecrease={() => {
                    let value = parseInt(quantity, 10);
                    if (isNaN(value) || value <= 1) value = 1;
                    else value = value - 1;
                    setQuantity(value.toString());
                    debouncedChangeQuantity(value);
                  }}
                />
              </QuantityWrapper>
            )}
            <PriceWrapper hasImages={hasImages}>
              <PriceLabel hasImages={hasImages}>{t('Price')}:</PriceLabel>
              {item?.gross_price !== item?.net_price && isGrossVisible && (
                <GrossPriceValue>
                  {formatMoney(item?.gross_price)}
                </GrossPriceValue>
              )}
              <NetPriceValue>{formatMoney(item?.net_price)}</NetPriceValue>
            </PriceWrapper>
          </BottomWrapper>
        </GridPriceWrapper>
      </ImagesConfigurationWrapper>
      <HorizontalLine hasImages={hasImages} />
    </Container>
  );
};
export default ShoppingCartCard;
